/** A loyalty checkout discount represents a discount applied to a checkout using loyalty rewards, coupons, or referral rewards. */
export interface LoyaltyCheckoutDiscount
  extends LoyaltyCheckoutDiscountDiscountOneOf {
  /**
   * ID of the loyalty reward used for this discount.
   * @format GUID
   */
  rewardId?: string;
  /**
   * ID of the loyalty coupon used for this discount.
   * @format GUID
   */
  loyaltyCouponId?: string;
  /**
   * ID of the referral reward used for this discount.
   * @format GUID
   */
  referralRewardId?: string;
  /**
   * Loyalty checkout discount ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /**
   * ID of the checkout this discount is applied to.
   * @format GUID
   */
  checkoutId?: string;
  /**
   * Current status of the discount.
   * @readonly
   */
  status?: DiscountStatusWithLiterals;
  /**
   * The type of discount applied.
   * @readonly
   */
  discountType?: DiscountTypeWithLiterals;
}

/** @oneof */
export interface LoyaltyCheckoutDiscountDiscountOneOf {
  /**
   * ID of the loyalty reward used for this discount.
   * @format GUID
   */
  rewardId?: string;
  /**
   * ID of the loyalty coupon used for this discount.
   * @format GUID
   */
  loyaltyCouponId?: string;
  /**
   * ID of the referral reward used for this discount.
   * @format GUID
   */
  referralRewardId?: string;
}

export enum DiscountStatus {
  /** Status is unknown or not specified. */
  UNKNOWN = 'UNKNOWN',
  /** Discount has been applied to the checkout. */
  APPLIED = 'APPLIED',
  /** Discount application is in progress. */
  IN_PROGRESS = 'IN_PROGRESS',
  /** Discount has been successfully completed. */
  COMPLETED = 'COMPLETED',
  /** Discount has been refunded. */
  REFUNDED = 'REFUNDED',
  /** Attempt to refund the discount failed. */
  FAILED_TO_REFUND = 'FAILED_TO_REFUND',
  /** Discount was refunded due to a timeout. */
  REFUNDED_DUE_TO_TIMEOUT = 'REFUNDED_DUE_TO_TIMEOUT',
  /** Discount was refunded due to manual removal. */
  REFUNDED_DUE_TO_MANUAL_DISCOUNT_REMOVAL = 'REFUNDED_DUE_TO_MANUAL_DISCOUNT_REMOVAL',
  /** Discount is scheduled for refund. */
  SCHEDULED_FOR_REFUND = 'SCHEDULED_FOR_REFUND',
  /** Discount wasn't used. */
  UNUSED = 'UNUSED',
}

/** @enumType */
export type DiscountStatusWithLiterals =
  | DiscountStatus
  | 'UNKNOWN'
  | 'APPLIED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'REFUNDED'
  | 'FAILED_TO_REFUND'
  | 'REFUNDED_DUE_TO_TIMEOUT'
  | 'REFUNDED_DUE_TO_MANUAL_DISCOUNT_REMOVAL'
  | 'SCHEDULED_FOR_REFUND'
  | 'UNUSED';

export enum DiscountType {
  /** Discount type is unknown or not specified. */
  UNKNOWN = 'UNKNOWN',
  /** A discount from a flexible reward. */
  DISCOUNT_AMOUNT = 'DISCOUNT_AMOUNT',
  /** A discount from a redeemed loyalty program coupon. */
  LOYALTY_COUPON = 'LOYALTY_COUPON',
  /** A discount from a coupon reward that hasn't yet been redeemed. */
  COUPON_REWARD = 'COUPON_REWARD',
  /** A discount from a referral reward. */
  REFERRAL_REWARD = 'REFERRAL_REWARD',
}

/** @enumType */
export type DiscountTypeWithLiterals =
  | DiscountType
  | 'UNKNOWN'
  | 'DISCOUNT_AMOUNT'
  | 'LOYALTY_COUPON'
  | 'COUPON_REWARD'
  | 'REFERRAL_REWARD';

export interface DiscountRefundedDueToManualDiscountRemoval {
  /** Loyalty checkout discount refunded due to manual removal. */
  discount?: LoyaltyCheckoutDiscount;
}

export interface DiscountRefundedDueToTimeout {
  /** Loyalty checkout discount refunded due to timeout. */
  discount?: LoyaltyCheckoutDiscount;
}

export interface DiscountScheduledForRefund {
  /** Loyalty checkout discount scheduled for refund. */
  discount?: LoyaltyCheckoutDiscount;
}

/** Request to apply a discount to a checkout. */
export interface ApplyDiscountToCheckoutRequest
  extends ApplyDiscountToCheckoutRequestDiscountOneOf {
  /** Unredeemed loyalty reward discount. */
  reward?: Reward;
  /** Loyalty coupon discount. */
  loyaltyCoupon?: LoyaltyCoupon;
  /** Referral reward discount. */
  referralReward?: ReferralReward;
  /**
   * ID of the checkout to apply the discount to.
   * @format GUID
   */
  checkoutId: string;
}

/** @oneof */
export interface ApplyDiscountToCheckoutRequestDiscountOneOf {
  /** Unredeemed loyalty reward discount. */
  reward?: Reward;
  /** Loyalty coupon discount. */
  loyaltyCoupon?: LoyaltyCoupon;
  /** Referral reward discount. */
  referralReward?: ReferralReward;
}

export interface Reward {
  /**
   * ID of the loyalty reward to be used for this discount.
   * @format GUID
   */
  id?: string;
  /**
   * Number of points to spend for this reward.
   * For example, if the reward is 10 points = $1, then 35 points would give a $3.50 discount.
   *
   * >**Note:** Only relevant when using a flexible type reward. Depending on how your loyalty program is set up, there may be a minimum number of points you must spend.
   * @min 1
   * @max 9999999
   */
  pointsToSpend?: number | null;
  /**
   * Revision number, which increments by 1 each time a reward is updated.
   * To prevent conflicting changes, the current `revision` must be passed when updating a reward.
   */
  revision?: string;
}

export interface LoyaltyCoupon {
  /**
   * ID of the loyalty coupon to be used for this discount.
   * @format GUID
   */
  id?: string;
  /**
   * Revision of the coupon object. Incremented by 1 each time the coupon object is updated.
   * Pass the latest `revision` when updating to prevent conflicting changes.
   */
  revision?: string;
}

export interface ReferralReward {
  /**
   * Referral reward discount ID.
   * @format GUID
   */
  id?: string;
  /**
   * Revision of the referral object. Incremented by 1 each time the referral object is updated.
   * Pass the latest `revision` when updating to prevent conflicting changes.
   */
  revision?: string;
}

/** Response after applying a discount to a checkout. */
export interface ApplyDiscountToCheckoutResponse {
  /** The applied loyalty checkout discount. */
  discount?: LoyaltyCheckoutDiscount;
}

export interface QueryLoyaltyCheckoutDiscountsRequest {
  /**
   * Filter object.
   *
   * See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more information.
   */
  query: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   * @maxSize 5
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /**
   * Cursor paging options.
   *
   * Learn more about [cursor paging](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#cursor-paging).
   */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
  /**
   * Origin point for geo-distance sorting on a GEO field
   * results are ordered by distance from this point (ASC = nearest first, DESC = farthest first).
   */
  origin?: AddressLocation;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

/** Response from querying loyalty checkout discounts. */
export interface QueryLoyaltyCheckoutDiscountsResponse {
  /** List of loyalty checkout discounts matching the query. */
  loyaltyCheckoutDiscounts?: LoyaltyCheckoutDiscount[];
  /** Paging metadata for the response. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface CursorPagingMetadata {
  /** Number of items returned in current page. */
  count?: number | null;
  /** Cursor strings that point to the next page, previous page, or both. */
  cursors?: Cursors;
  /**
   * Whether there are more pages to retrieve following the current page.
   *
   * + `true`: Another page of results can be retrieved.
   * + `false`: This is the last page.
   */
  hasNext?: boolean | null;
  /**
   * Total number of items matching the filter.
   * Available only on the first page of *Search* results, not included in *Query* or *List* results.
   * If the Search results span multiple pages, the value of `total` will exceed the number of items returned on the first page.
   * @internal
   */
  total?: number | null;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /**
   * Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity
   * @internal
   */
  triggeredByUndelete?: boolean | null;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
  /**
   * This field is currently part of the of the EntityUpdatedEvent msg, but scala/node libraries which implements the domain events standard
   * wont populate it / have any reference to it in the API.
   * The main reason for it is that fetching the old entity from the DB will have a performance hit on an update operation so unless truly needed,
   * the developer should send only the new (current) entity.
   * An additional reason is not wanting to send this additional entity over the wire (kafka) since in some cases it can be really big
   * Developers that must reflect the old entity will have to implement their own domain event sender mechanism which will follow the DomainEvent proto message.
   * @internal
   * @deprecated
   */
  previousEntityAsJson?: string | null;
  /**
   * Map of fully qualified field paths to their previous values for fields that changed.
   * For more details please see [AIP](https://dev.wix.com/docs/rnd-general/articles/p13n-guidelines-aips/guidance-aips/design-patterns/7016-events#modified-fields-format)
   * @internal
   */
  modifiedFields?: Record<string, any>;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface EntityDeletedEvent {
  /**
   * Indicates if the entity is sent to trash-bin. only available when trash-bin is enabled
   * @internal
   */
  movedToTrash?: boolean | null;
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
  /**
   * Optional domain-specific metadata defined by the API owner, encoded as JSON.
   * @internal
   */
  additionalMetadataAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface Empty {}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}
